import { useState } from 'react';
import { Download, Upload, Lock, Unlock, Key, AlertCircle, CheckCircle, FileJson } from 'lucide-react';
import { db } from '../lib/database';
import { encryptData, decryptData, validatePassphrase, generateSecurePassphrase, estimateCrackTime } from '../lib/encryption';

export function BackupPanel() {
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Restore state
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restorePassphrase, setRestorePassphrase] = useState('');
  const [restoreEncrypted, setRestoreEncrypted] = useState(false);

  const passphraseValidation = passphrase ? validatePassphrase(passphrase) : null;
  const crackTime = passphrase ? estimateCrackTime(passphrase) : '';

  const handleGeneratePassphrase = () => {
    const generated = generateSecurePassphrase(20);
    setPassphrase(generated);
    setConfirmPassphrase(generated);
    setShowPassphrase(true);
  };

  const handleExportBackup = async () => {
    if (isEncrypted && !passphrase) {
      setStatus({ type: 'error', message: 'Please enter a passphrase for encryption' });
      return;
    }

    if (isEncrypted && passphrase !== confirmPassphrase) {
      setStatus({ type: 'error', message: 'Passphrases do not match' });
      return;
    }

    if (isEncrypted && passphraseValidation && !passphraseValidation.valid) {
      setStatus({ type: 'error', message: passphraseValidation.message });
      return;
    }

    setIsProcessing(true);
    setStatus(null);

    try {
      // Export all data
      const data = await db.exportAllData();
      let finalData = data;
      let filename = `chromis-backup-${new Date().toISOString().split('T')[0]}.json`;

      // Encrypt if requested
      if (isEncrypted && passphrase) {
        const encrypted = await encryptData(data, passphrase);
        finalData = JSON.stringify(encrypted, null, 2);
        filename = `chromis-backup-encrypted-${new Date().toISOString().split('T')[0]}.json`;
      }

      // Download file
      const blob = new Blob([finalData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setStatus({ 
        type: 'success', 
        message: `Backup exported successfully${isEncrypted ? ' (encrypted)' : ''}` 
      });
      
      // Clear sensitive data
      setPassphrase('');
      setConfirmPassphrase('');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setStatus({ type: 'error', message: `Export failed: ${errorMessage}` });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setRestoreFile(file);
      setStatus(null);
      
      // Try to detect if file is encrypted by checking content
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const content = event.target?.result as string;
          const parsed = JSON.parse(content);
          // Check if it has encrypted data structure
          setRestoreEncrypted(parsed.ciphertext && parsed.iv && parsed.salt && parsed.version);
        } catch {
          setStatus({ type: 'error', message: 'Invalid backup file format' });
        }
      };
      reader.readAsText(file);
    }
  };

  const handleRestoreBackup = async () => {
    if (!restoreFile) {
      setStatus({ type: 'error', message: 'Please select a backup file' });
      return;
    }

    if (restoreEncrypted && !restorePassphrase) {
      setStatus({ type: 'error', message: 'This backup is encrypted. Please enter the passphrase.' });
      return;
    }

    setIsProcessing(true);
    setStatus(null);

    try {
      const text = await restoreFile.text();
      let data = text;

      // Decrypt if encrypted
      if (restoreEncrypted) {
        const encrypted = JSON.parse(text);
        data = await decryptData(encrypted, restorePassphrase);
      }

      // Validate and import
      const parsed = JSON.parse(data);
      if (!parsed.palettes && !parsed.colors) {
        throw new Error('Invalid backup format - missing required data');
      }

      await db.importAllData(data);

      setStatus({ 
        type: 'success', 
        message: `Backup restored successfully! Imported ${parsed.palettes?.length || 0} palettes.` 
      });
      
      // Clear form
      setRestoreFile(null);
      setRestorePassphrase('');
      setRestoreEncrypted(false);
      
      // Reset file input
      const fileInput = document.getElementById('restore-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setStatus({ type: 'error', message: `Restore failed: ${errorMessage}` });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Export Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Download className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Export Backup
          </h3>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Export all your palettes, colors, history, and settings to a JSON file. 
          Optionally encrypt the backup with a passphrase for security.
        </p>

        {/* Encryption Toggle */}
        <div className="mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isEncrypted}
              onChange={(e) => setIsEncrypted(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <Lock className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Encrypt backup with passphrase
            </span>
          </label>
        </div>

        {/* Passphrase Fields */}
        {isEncrypted && (
          <div className="space-y-4 mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Passphrase
              </label>
              <div className="relative">
                <input
                  type={showPassphrase ? 'text' : 'password'}
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  placeholder="Enter a strong passphrase"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassphrase(!showPassphrase)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  {showPassphrase ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                </button>
              </div>
              {passphraseValidation && (
                <div className="mt-2 space-y-1">
                  <p className={`text-xs ${
                    passphraseValidation.strength === 'strong' ? 'text-green-600' :
                    passphraseValidation.strength === 'medium' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {passphraseValidation.message}
                  </p>
                  {crackTime && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Estimated crack time: {crackTime}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm Passphrase
              </label>
              <input
                type={showPassphrase ? 'text' : 'password'}
                value={confirmPassphrase}
                onChange={(e) => setConfirmPassphrase(e.target.value)}
                placeholder="Re-enter passphrase"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {passphrase && confirmPassphrase && passphrase !== confirmPassphrase && (
                <p className="mt-1 text-xs text-red-600">Passphrases do not match</p>
              )}
            </div>

            <button
              onClick={handleGeneratePassphrase}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <Key className="w-4 h-4" />
              Generate secure passphrase
            </button>

            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-xs text-yellow-800 dark:text-yellow-200">
                <strong>Important:</strong> Store your passphrase securely. Without it, you cannot restore this backup.
              </p>
            </div>
          </div>
        )}

        <button
          onClick={handleExportBackup}
          disabled={isProcessing || (isEncrypted && (!passphrase || passphrase !== confirmPassphrase))}
          className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          <Download className="w-4 h-4" />
          {isProcessing ? 'Exporting...' : 'Export Backup'}
        </button>
      </div>

      {/* Import Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Upload className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Restore Backup
          </h3>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Restore your data from a previously exported backup file. This will merge with your existing data.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Backup File
            </label>
            <input
              id="restore-file-input"
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="w-full text-sm text-gray-900 dark:text-gray-100 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/20 dark:file:text-blue-400"
            />
            {restoreFile && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <FileJson className="w-4 h-4" />
                <span>{restoreFile.name}</span>
                {restoreEncrypted && (
                  <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 rounded text-xs font-medium">
                    Encrypted
                  </span>
                )}
              </div>
            )}
          </div>

          {restoreEncrypted && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Passphrase
              </label>
              <input
                type="password"
                value={restorePassphrase}
                onChange={(e) => setRestorePassphrase(e.target.value)}
                placeholder="Enter backup passphrase"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          <button
            onClick={handleRestoreBackup}
            disabled={isProcessing || !restoreFile || (restoreEncrypted && !restorePassphrase)}
            className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {isProcessing ? 'Restoring...' : 'Restore Backup'}
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {status && (
        <div className={`p-4 rounded-lg border flex items-start gap-3 ${
          status.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
            : status.type === 'error'
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
        }`}>
          {status.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={`text-sm ${
            status.type === 'success' 
              ? 'text-green-800 dark:text-green-200' 
              : status.type === 'error'
              ? 'text-red-800 dark:text-red-200'
              : 'text-blue-800 dark:text-blue-200'
          }`}>
            {status.message}
          </p>
        </div>
      )}
    </div>
  );
}
